import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { 
  Plus, 
  Minus, 
  Trash, 
  X,
  Check,
  MagnifyingGlass,
  Receipt,
  CreditCard,
  Money,
  Wallet,
  Note,
  MapPin,
  CircleNotch,
  Printer,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { printReceipt } from '@/components/ReceiptPrinter';
import { useSettings } from '@/context/SettingsContext';
import axios from 'axios';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

export const POSPage = () => {
  const { currentOutlet, categories, menuItems, modifierGroups, createOrder, processPayment } = useApp();
  const { settings: appSettings } = useSettings();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState([]);
  const [upsell, setUpsell] = useState([]); // AI upsell suggestions for last-added item
  const [searchQuery, setSearchQuery] = useState('');
  const [orderType, setOrderType] = useState('dine_in');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [showModifierModal, setShowModifierModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0].id);
    }
  }, [categories, selectedCategory]);

  // Debounced delivery distance lookup (OpenStreetMap, free)
  useEffect(() => {
    if (orderType !== 'delivery' || !currentOutlet?.id) {
      setDeliveryQuote(null);
      setQuoteError('');
      return;
    }
    const addr = deliveryAddress.trim();
    if (addr.length < 5) {
      setDeliveryQuote(null);
      setQuoteError('');
      return;
    }
    const ctrl = new AbortController();
    setQuoteLoading(true);
    setQuoteError('');
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/delivery/quote`, {
          params: { outlet_id: currentOutlet.id, address: addr },
          withCredentials: true,
          signal: ctrl.signal,
        });
        setDeliveryQuote(r.data);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') {
          setDeliveryQuote(null);
          setQuoteError(e.response?.data?.detail || 'Could not locate this address');
        }
      } finally {
        setQuoteLoading(false);
      }
    }, 800);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [deliveryAddress, orderType, currentOutlet?.id]);

  const filteredItems = menuItems.filter(item => {
    const categoryMatch = selectedCategory ? item.category_id === selectedCategory : true;
    const searchMatch = searchQuery 
      ? item.name.toLowerCase().includes(searchQuery.toLowerCase())
      : true;
    return categoryMatch && searchMatch && item.is_available;
  });

  const quickAddById = (mid) => {
    const m = menuItems.find((x) => x.id === mid);
    if (m) addToCart(m);
  };

  const fetchUpsellFor = useCallback(async (itemId) => {
    if (!currentOutlet?.id || !itemId) return;
    if (!appSettings?.pos?.auto_collapse_modifiers && appSettings?.ai && appSettings.ai.pos_upsell_popup === false) {
      setUpsell([]); return;
    }
    try {
      const r = await axios.get(
        `${API_URL}/api/ai/upsell?outlet_id=${currentOutlet.id}&item_id=${itemId}`,
        { withCredentials: true });
      setUpsell(r.data?.suggestions || []);
    } catch (e) { setUpsell([]); }
  }, [currentOutlet?.id, appSettings]);

  const addToCart = async (item) => {
    // Check if item has modifiers
    if (item.modifier_group_ids && item.modifier_group_ids.length > 0) {
      // Fetch full item details with modifier groups
      try {
        const response = await axios.get(`${API_URL}/api/menu-items/${item.id}`, { withCredentials: true });
        setSelectedItem(response.data);
        setSelectedModifiers({});
        setShowModifierModal(true);
      } catch (e) {
        console.error('Error fetching item details:', e);
      }
    } else {
      // Add directly to cart
      const existingIndex = cart.findIndex(c => c.menu_item_id === item.id && !c.modifiers?.length);
      if (existingIndex >= 0) {
        const newCart = [...cart];
        newCart[existingIndex].quantity += 1;
        setCart(newCart);
      } else {
        setCart([...cart, {
          menu_item_id: item.id,
          name: item.name,
          price: getItemPrice(item),
          quantity: 1,
          modifiers: [],
          modifier_total: 0,
          notes: '',
        }]);
      }
      fetchUpsellFor(item.id);
    }
  };

  const getItemPrice = (item) => {
    switch (orderType) {
      case 'takeaway': return item.takeaway_price || item.dine_in_price;
      case 'delivery': return item.delivery_price || item.dine_in_price;
      default: return item.dine_in_price;
    }
  };

  const addItemWithModifiers = () => {
    if (!selectedItem) return;

    const modifiers = [];
    let modifierTotal = 0;

    Object.entries(selectedModifiers).forEach(([groupId, options]) => {
      const group = selectedItem.modifier_groups?.find(g => g.id === groupId);
      if (group && options) {
        const selectedOptions = Array.isArray(options) ? options : [options];
        selectedOptions.forEach(opt => {
          modifiers.push({
            group_name: group.name,
            option_name: opt.name,
            price: opt.price || 0,
          });
          modifierTotal += opt.price || 0;
        });
      }
    });

    setCart([...cart, {
      menu_item_id: selectedItem.id,
      name: selectedItem.name,
      price: getItemPrice(selectedItem),
      quantity: 1,
      modifiers,
      modifier_total: modifierTotal,
      notes: '',
    }]);

    setShowModifierModal(false);
    setSelectedItem(null);
    setSelectedModifiers({});
  };

  const updateQuantity = (index, delta) => {
    const newCart = [...cart];
    newCart[index].quantity += delta;
    if (newCart[index].quantity <= 0) {
      newCart.splice(index, 1);
    }
    setCart(newCart);
  };

  const removeFromCart = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    setCart(newCart);
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => sum + (item.price + item.modifier_total) * item.quantity, 0);
  };

  const calculateTax = () => {
    const taxRate = currentOutlet?.tax_rate || 0;
    return calculateSubtotal() * (taxRate / 100);
  };

  const calculateDeliveryFee = () => {
    if (orderType !== 'delivery' || !deliveryQuote || deliveryQuote.out_of_zone) return 0;
    return deliveryQuote.delivery_fee || 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax() + calculateDeliveryFee();
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) return;

    // Validate scheduling for takeaway / delivery
    let promisedISO = null;
    if (orderType === 'takeaway' || orderType === 'delivery') {
      if (!scheduledDate || !scheduledTime) {
        toast.error(`Please select a ${orderType === 'delivery' ? 'delivery' : 'pickup'} date and time`);
        return;
      }
      const combined = new Date(`${scheduledDate}T${scheduledTime}`);
      if (Number.isNaN(combined.getTime())) {
        toast.error('Invalid date/time');
        return;
      }
      if (combined.getTime() < Date.now() - 60_000) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      promisedISO = combined.toISOString();
    }
    if (orderType === 'delivery' && !deliveryAddress.trim()) {
      toast.error('Please enter a delivery address');
      return;
    }
    if (orderType === 'delivery' && deliveryQuote?.out_of_zone) {
      toast.error(`This address is outside the ${deliveryQuote.max_mi} mi delivery zone`);
      return;
    }

    setProcessing(true);
    try {
      const orderData = {
        order_type: orderType,
        customer_name: customerName,
        customer_phone: customerPhone,
        notes,
        promised_time: promisedISO,
        delivery_address: orderType === 'delivery' ? deliveryAddress.trim() : null,
        items: cart.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          modifiers: item.modifiers,
          notes: item.notes,
        })),
      };

      const order = await createOrder(orderData);
      setLastOrder(order);
      setShowPaymentModal(true);
    } catch (e) {
      console.error('Error creating order:', e);
    } finally {
      setProcessing(false);
    }
  };

  const [paidOrder, setPaidOrder] = useState(null);

  const handlePayment = async () => {
    if (!lastOrder) return;

    setProcessing(true);
    try {
      await processPayment({
        order_id: lastOrder.id,
        amount: lastOrder.total,
        method: paymentMethod,
        tip: 0,
      });

      // Hold on to the order so we can print it
      const completed = {
        ...lastOrder,
        payment_method: paymentMethod,
        cashier_name: lastOrder.cashier_name,
      };
      setPaidOrder(completed);

      // Auto-print if the outlet has it enabled
      if (appSettings?.pos?.auto_print_receipt) {
        printReceipt({ order: completed, outlet: currentOutlet, settings: appSettings });
      }
      toast.success(`Order #${completed.order_number} completed`);
    } catch (e) {
      console.error('Error processing payment:', e);
    } finally {
      setProcessing(false);
    }
  };

  const finishOrder = () => {
    setCart([]);
    setCustomerName('');
    setCustomerPhone('');
    setNotes('');
    setScheduledDate('');
    setScheduledTime('');
    setDeliveryAddress('');
    setDeliveryQuote(null);
    setShowPaymentModal(false);
    setLastOrder(null);
    setPaidOrder(null);
    setAmountReceived('');
  };

  const handleReprint = () => {
    if (paidOrder) {
      printReceipt({ order: paidOrder, outlet: currentOutlet, settings: appSettings });
    }
  };

  const handleModifierSelect = (groupId, option, isMulti) => {
    if (isMulti) {
      const current = selectedModifiers[groupId] || [];
      const exists = current.find(o => o.id === option.id);
      if (exists) {
        setSelectedModifiers({
          ...selectedModifiers,
          [groupId]: current.filter(o => o.id !== option.id)
        });
      } else {
        setSelectedModifiers({
          ...selectedModifiers,
          [groupId]: [...current, option]
        });
      }
    } else {
      setSelectedModifiers({
        ...selectedModifiers,
        [groupId]: option
      });
    }
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to use POS</p>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-4" data-testid="pos-page">
      {/* Left Panel - Menu */}
      <div
        className="flex-1 flex flex-col rounded-lg overflow-hidden"
        style={{
          backgroundColor: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
          color: 'var(--theme-text)',
        }}
      >
        {/* Search & Order Type */}
        <div className="p-4 space-y-4" style={{ borderBottom: '1px solid var(--theme-border)' }}>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search menu items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12"
              data-testid="pos-search"
            />
          </div>
          
          <div className="flex gap-2">
            {['dine_in', 'takeaway', 'delivery'].map((type) => (
              <button
                key={type}
                onClick={() => setOrderType(type)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  orderType === type 
                    ? 'text-white border-transparent' 
                    : 'hover:opacity-90'
                }`}
                style={
                  orderType === type
                    ? { backgroundColor: 'var(--theme-accent)' }
                    : {
                        backgroundColor: 'var(--theme-surface)',
                        color: 'var(--theme-text)',
                        borderColor: 'var(--theme-border)',
                      }
                }
                data-testid={`order-type-${type}`}
              >
                {type.replace('_', ' ').toUpperCase()}
              </button>
            ))}
          </div>

          {/* Scheduling + Delivery details */}
          {(orderType === 'takeaway' || orderType === 'delivery') && (
            <div
              className="rounded-lg p-3 space-y-3"
              style={{
                border: '1px solid var(--theme-accent)',
                backgroundColor: 'rgba(var(--theme-accent-rgb, 249, 115, 22), 0.08)',
              }}
              data-testid="schedule-panel"
            >
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--theme-accent)' }}>
                {orderType === 'delivery' ? 'Delivery Schedule & Address' : 'Pickup Schedule'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs" style={{ color: 'var(--theme-text)' }}>Date</Label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="h-10 [color-scheme:dark]"
                    style={{
                      backgroundColor: 'var(--theme-surface)',
                      color: 'var(--theme-text)',
                      borderColor: 'var(--theme-border)',
                    }}
                    data-testid="schedule-date"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs" style={{ color: 'var(--theme-text)' }}>Time</Label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="h-10 [color-scheme:dark]"
                    style={{
                      backgroundColor: 'var(--theme-surface)',
                      color: 'var(--theme-text)',
                      borderColor: 'var(--theme-border)',
                    }}
                    data-testid="schedule-time"
                  />
                </div>
              </div>
              {orderType === 'delivery' && (
                <div className="space-y-2">
                  <Label className="text-xs">Delivery Address</Label>
                  <Textarea
                    placeholder="Street, Apt/Suite, City, Zip…"
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    rows={2}
                    className="text-sm"
                    data-testid="delivery-address"
                  />
                  {/* Distance + Map */}
                  {quoteLoading && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="quote-loading">
                      <CircleNotch size={14} className="animate-spin" />
                      Calculating distance…
                    </div>
                  )}
                  {!quoteLoading && quoteError && (
                    <p className="text-xs text-red-500" data-testid="quote-error">{quoteError}</p>
                  )}
                  {!quoteLoading && deliveryQuote && (
                    <div
                      className={`rounded-lg border overflow-hidden ${
                        deliveryQuote.out_of_zone
                          ? 'border-red-400 bg-red-50'
                          : 'border-primary/40 bg-white'
                      }`}
                      data-testid="quote-result"
                    >
                      <div className="px-3 py-2 flex items-center justify-between gap-3 text-xs font-medium">
                        <div className={`flex items-center gap-2 ${deliveryQuote.out_of_zone ? 'text-red-600' : 'text-primary'}`}>
                          <MapPin size={14} weight="fill" />
                          <span data-testid="quote-distance">{deliveryQuote.distance_miles} mi</span>
                        </div>
                        <span className="text-muted-foreground" data-testid="quote-eta">
                          ≈ {deliveryQuote.estimated_minutes} min
                        </span>
                      </div>
                      {deliveryQuote.out_of_zone ? (
                        <p className="px-3 py-2 text-xs font-medium text-red-700 bg-red-100" data-testid="out-of-zone">
                          Sorry — this address is outside our {deliveryQuote.max_mi} mi delivery zone.
                        </p>
                      ) : (
                        <p className="px-3 py-2 text-xs font-medium text-primary bg-primary/5 flex justify-between" data-testid="quote-fee">
                          <span>Delivery fee</span>
                          <span>{deliveryQuote.currency_symbol || '$'}{deliveryQuote.delivery_fee?.toFixed(2)}</span>
                        </p>
                      )}
                      <iframe
                        title="Delivery route preview"
                        className="w-full h-40 border-t border-primary/20"
                        loading="lazy"
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${
                          Math.min(deliveryQuote.outlet.lng, deliveryQuote.customer.lng) - 0.01
                        }%2C${
                          Math.min(deliveryQuote.outlet.lat, deliveryQuote.customer.lat) - 0.01
                        }%2C${
                          Math.max(deliveryQuote.outlet.lng, deliveryQuote.customer.lng) + 0.01
                        }%2C${
                          Math.max(deliveryQuote.outlet.lat, deliveryQuote.customer.lat) + 0.01
                        }&layer=mapnik&marker=${deliveryQuote.customer.lat}%2C${deliveryQuote.customer.lng}`}
                        data-testid="delivery-map"
                      />
                      <p className="px-3 py-1.5 text-[10px] text-muted-foreground truncate" title={deliveryQuote.customer.address}>
                        {deliveryQuote.customer.address}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="flex gap-2 p-4 overflow-x-auto scrollbar-hide" style={{ borderBottom: '1px solid var(--theme-border)' }}>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors border ${
                selectedCategory === cat.id 
                  ? 'text-white border-transparent' 
                  : 'hover:opacity-90'
              }`}
              style={
                selectedCategory === cat.id
                  ? { backgroundColor: 'var(--theme-accent)' }
                  : {
                      backgroundColor: 'var(--theme-surface)',
                      color: 'var(--theme-text)',
                      borderColor: 'var(--theme-border)',
                    }
              }
              data-testid={`category-${cat.name.toLowerCase()}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Menu Items Grid */}
        <ScrollArea className="flex-1 p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {filteredItems.map((item) => (
              <button
                key={item.id}
                onClick={() => addToCart(item)}
                className="pos-card p-4 text-left transition-all touch-manipulation"
                style={{ color: 'var(--theme-text)' }}
                data-testid={`menu-item-${item.id}`}
              >
                {item.image_url && (
                  <div className="aspect-square rounded-lg overflow-hidden mb-3" style={{ backgroundColor: 'var(--theme-background)' }}>
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <h3 className="font-medium text-sm line-clamp-2" style={{ color: 'var(--theme-text)' }}>{item.name}</h3>
                <p className="font-bold mt-1" style={{ color: 'var(--theme-accent)' }}>${getItemPrice(item).toFixed(2)}</p>
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Right Panel - Cart */}
      <div
        className="w-96 rounded-lg flex flex-col"
        style={{
          backgroundColor: 'var(--theme-surface)',
          border: '1px solid var(--theme-border)',
          color: 'var(--theme-text)',
        }}
      >
        <div className="p-4" style={{ borderBottom: '1px solid var(--theme-border)' }}>
          <h2 className="font-heading text-lg font-bold flex items-center gap-2" style={{ color: 'var(--theme-text)' }}>
            <Receipt size={20} />
            Current Order
          </h2>
        </div>

        {/* Cart Items */}
        <ScrollArea className="flex-1 p-4">
          {cart.length === 0 ? (
            <div className="text-center py-8" style={{ color: 'var(--theme-text-muted)' }}>
              <Receipt size={48} className="mx-auto mb-4 opacity-30" />
              <p>No items in cart</p>
              <p className="text-sm">Tap items to add them</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((item, index) => (
                <div key={index} className="rounded-lg p-3" style={{ backgroundColor: 'var(--theme-background)' }}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm" style={{ color: 'var(--theme-text)' }}>{item.name}</h4>
                      {item.modifiers.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {item.modifiers.map((mod, i) => (
                            <p key={i} className="text-xs" style={{ color: 'var(--theme-text-muted)' }}>
                              + {mod.option_name} {mod.price > 0 && `($${mod.price.toFixed(2)})`}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => removeFromCart(index)}
                      className="p-1 hover:text-red-500"
                      style={{ color: 'var(--theme-text-muted)' }}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(index, -1)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:opacity-80"
                        style={{ backgroundColor: 'var(--theme-surface)', border: '1px solid var(--theme-border)', color: 'var(--theme-text)' }}
                        data-testid={`decrease-${index}`}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-8 text-center font-medium" style={{ color: 'var(--theme-text)' }}>{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(index, 1)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:opacity-80"
                        style={{ backgroundColor: 'var(--theme-surface)', border: '1px solid var(--theme-border)', color: 'var(--theme-text)' }}
                        data-testid={`increase-${index}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                    <span className="font-bold" style={{ color: 'var(--theme-text)' }}>
                      ${((item.price + item.modifier_total) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* AI upsell suggestions */}
        {upsell.length > 0 && (
          <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--theme-border)' }} data-testid="pos-upsell">
            <p className="text-[10px] uppercase tracking-widest mb-2 flex items-center gap-1" style={{ color: 'var(--theme-text-muted)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
              AI Suggested Add-ons
            </p>
            <div className="flex flex-wrap gap-2">
              {upsell.map((u) => (
                <button
                  key={u.menu_item_id}
                  onClick={() => quickAddById(u.menu_item_id)}
                  className="px-3 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-br from-orange-500 to-red-500 hover:scale-105 transition-transform shadow"
                  data-testid={`upsell-${u.menu_item_id}`}
                  title={`Bought together ${u.co_occurrences || ''} time(s)`}
                >
                  + {u.name}
                </button>
              ))}
              <button
                onClick={() => setUpsell([])}
                className="px-2 py-2 rounded-xl text-xs"
                style={{ color: 'var(--theme-text-muted)' }}
                title="Hide suggestions"
              >×</button>
            </div>
          </div>
        )}

        {/* Customer Info */}
        <div className="p-4 space-y-3" style={{ borderTop: '1px solid var(--theme-border)' }}>
          <Input
            placeholder="Customer name (optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="h-10"
            data-testid="customer-name"
          />
          <Input
            placeholder="Phone number (optional)"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            className="h-10"
            data-testid="customer-phone"
          />
        </div>

        {/* Totals */}
        <div className="p-4 space-y-2 text-sm" style={{ borderTop: '1px solid var(--theme-border)', color: 'var(--theme-text)' }}>
          <div className="flex justify-between">
            <span style={{ color: 'var(--theme-text-muted)' }}>Subtotal</span>
            <span>${calculateSubtotal().toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: 'var(--theme-text-muted)' }}>Tax ({currentOutlet?.tax_rate || 0}%)</span>
            <span>${calculateTax().toFixed(2)}</span>
          </div>
          {orderType === 'delivery' && deliveryQuote && !deliveryQuote.out_of_zone && (
            <div className="flex justify-between" data-testid="cart-delivery-fee">
              <span style={{ color: 'var(--theme-text-muted)' }}>
                Delivery ({deliveryQuote.distance_miles} mi)
              </span>
              <span>${calculateDeliveryFee().toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold pt-2" style={{ borderTop: '1px solid var(--theme-border)' }}>
            <span>Total</span>
            <span style={{ color: 'var(--theme-accent)' }}>${calculateTotal().toFixed(2)}</span>
          </div>
        </div>

        {/* Place Order Button */}
        <div className="p-4" style={{ borderTop: '1px solid var(--theme-border)' }}>
          <Button
            onClick={handlePlaceOrder}
            disabled={cart.length === 0 || processing || (orderType === 'delivery' && deliveryQuote?.out_of_zone)}
            className="w-full h-14 text-white text-lg font-bold disabled:opacity-50"
            style={{ backgroundColor: 'var(--theme-accent)' }}
            data-testid="place-order-btn"
          >
            {processing
              ? 'Processing...'
              : (orderType === 'delivery' && deliveryQuote?.out_of_zone)
                ? 'Out of Delivery Zone'
                : `Place Order • $${calculateTotal().toFixed(2)}`}
          </Button>
        </div>
      </div>

      {/* Modifier Modal */}
      <Dialog open={showModifierModal} onOpenChange={setShowModifierModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{selectedItem?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {selectedItem?.modifier_groups?.map((group) => (
              <div key={group.id} className="space-y-3">
                <div>
                  <h3 className="font-medium">{group.name}</h3>
                  <p className="text-xs text-muted-foreground">
                    {group.is_required ? 'Required • ' : 'Optional • '}
                    {group.selection_type === 'single' ? 'Choose one' : `Choose up to ${group.max_selections}`}
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  {group.options?.map((option) => {
                    const isSelected = group.selection_type === 'single'
                      ? selectedModifiers[group.id]?.id === option.id
                      : selectedModifiers[group.id]?.find(o => o.id === option.id);
                    
                    return (
                      <button
                        key={option.id}
                        onClick={() => handleModifierSelect(group.id, option, group.selection_type === 'multi')}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          isSelected 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`modifier-${option.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{option.name}</span>
                          {isSelected && <Check size={16} className="text-primary" />}
                        </div>
                        {option.price > 0 && (
                          <span className="text-xs text-muted-foreground">+${option.price.toFixed(2)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowModifierModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={addItemWithModifiers}
              className="flex-1 bg-primary hover:bg-primary-hover text-white"
              data-testid="add-with-modifiers-btn"
            >
              Add to Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => { if (!open) finishOrder(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {paidOrder ? 'Payment Complete' : 'Payment'}
            </DialogTitle>
          </DialogHeader>

          {/* ---- Post-payment receipt actions ---- */}
          {paidOrder && (
            <div className="space-y-5 py-2" data-testid="payment-success">
              <div className="text-center">
                <div className="mx-auto w-14 h-14 rounded-full bg-accent-green/15 flex items-center justify-center mb-2">
                  <Check size={28} className="text-accent-green" />
                </div>
                <p className="text-sm text-muted-foreground">Order #{paidOrder.order_number}</p>
                <p className="text-4xl font-heading font-bold mt-1">${paidOrder.total.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground mt-1 capitalize">Paid via {paidOrder.payment_method}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleReprint}
                  className="h-12 bg-primary hover:bg-primary-hover text-white"
                  data-testid="print-receipt-btn"
                >
                  <Printer size={18} className="mr-2" /> Print / Save PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={finishOrder}
                  className="h-12"
                  data-testid="new-order-btn"
                >
                  <Plus size={18} className="mr-2" /> New Order
                </Button>
              </div>
              <p className="text-[11px] text-center text-muted-foreground">
                Tip: in the print dialog, choose "Save as PDF" as the destination.
              </p>
            </div>
          )}

          {/* ---- Payment selection (pre-payment) ---- */}
          {!paidOrder && lastOrder && (
            <div className="space-y-6 py-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Order #{lastOrder.order_number}</p>
                <p className="text-4xl font-heading font-bold mt-2">${lastOrder.total.toFixed(2)}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'cash', icon: Money, label: 'Cash' },
                  { id: 'card', icon: CreditCard, label: 'Card' },
                  { id: 'digital', icon: Wallet, label: 'Digital' },
                  { id: 'split', icon: Note, label: 'Split' },
                ].map((method) => (
                  <button
                    key={method.id}
                    onClick={() => setPaymentMethod(method.id)}
                    className={`p-4 rounded-lg border flex flex-col items-center gap-2 transition-all ${
                      paymentMethod === method.id 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`payment-${method.id}`}
                  >
                    <method.icon size={24} />
                    <span className="text-sm font-medium">{method.label}</span>
                  </button>
                ))}
              </div>

              {paymentMethod === 'cash' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Amount Received</label>
                  <Input
                    type="number"
                    value={amountReceived}
                    onChange={(e) => setAmountReceived(e.target.value)}
                    placeholder={`$${lastOrder.total.toFixed(2)}`}
                    className="h-12 text-lg"
                    data-testid="amount-received"
                  />
                  {amountReceived && parseFloat(amountReceived) >= lastOrder.total && (
                    <p className="text-accent-green font-medium">
                      Change: ${(parseFloat(amountReceived) - lastOrder.total).toFixed(2)}
                    </p>
                  )}
                </div>
              )}

              <Button
                onClick={handlePayment}
                disabled={processing}
                className="w-full h-14 bg-accent-green hover:bg-green-600 text-white text-lg font-bold"
                data-testid="complete-payment-btn"
              >
                {processing ? 'Processing...' : 'Complete Payment'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSPage;
